import axios, { AxiosResponse } from 'axios';
import { ICourseExportPackages, INewPageModel, TCourseViewModel } from '../models/CourseViewModels';
import { EOwnerCourseListSortByType, ICopyPageObject, TOwnerCourseViewModel } from '../models/OwnerViewModel';
import {
  EContentType,
  IContent,
  IContentProperties,
  ICopyObject,
  ICssCaption,
  IEnquiryQuestionProperties,
  IPagePlaceholder,
  IPageProperties,
  IPlaceholderProperties,
  TPageViewModel,
} from '../models/PageViewModel';
import { EnquiryContentProperties, EnquiryProperties, ICourseProperties, IPropertyValueObject, IValueMapID } from '../models/PropertyViewModel';
import { ICourseWizard /*, TWizard*/ } from '../models/WizardViewModel';
import SnackbarUtils from '../../components/snackbarUtils/SnackbarUtilsConfigurator';
import { ILocalPathModel, IUploadFileResponseModel, LSResponseModel } from '../models/MediaLibraryModels';
import { IMetadata, IUserPasswordChangeResponse, IUserProfileViewModel, IWhoAmIResponse } from '../models/UserProfileViewModel';
import { CreatorUser } from '../../store/user/userStore';
import { store } from '../../store/store';
import { EPageState } from '../../store/pageStore';
import { ICoursePageExportData } from '../models/ExportHTMLModels';
import { IEnquiryContentObjectProperties } from '../models/enquiry/EnquiryContentObjectModel';
import { IOwnerIdModel, IRoles, ITestOwner, IUser } from '../../store/adminStore';
import { IMoveEnquiryObj } from '../../store/enquiryStore';
import { ISharedUsers } from '../../features/courseOverview/courseItems/CourseItemFeature';
import { IFindOwner } from '../models/AgentModels';
import { ECourseViewType } from '../../store/ownerStore';
import { createBrowserHistory } from 'history';
import { timeout } from '../../utils/timeout';
import type { GoogleSynthesisRequest, GoogleVoiceCatalogApiResponse, GoogleVoiceOption } from '../../components/aiVoice/google/types';
import { IPreviewComment, IPreviewCommentReaction } from '../models/PreviewCommentModels';
import { isSessionSuperseded } from './sessionSuperseded';

export let accessToken = '';
let logoutHandler: (() => void) | null = null;

export const setAccessToken = (aToken: string) => {
  accessToken = aToken;
};

let isConnectionSnackbar = false;
export const history = createBrowserHistory();

if (process.env.REACT_APP_ENV_API != undefined) {
  axios.defaults.baseURL = process.env.REACT_APP_ENV_API + '/niasAPI';
} else {
  axios.defaults.baseURL = 'https://creatordev.norskinteraktiv.no/niasAPI';
}

const normalizeBaseUrl = (baseUrl: string) => baseUrl.replace(/\/+$/, '');

const resolvePreviewApiBaseURL = () => {
  const explicitPreviewApiBaseURL = process.env.REACT_APP_ENV_PREVIEW_API?.trim();
  if (explicitPreviewApiBaseURL) {
    return normalizeBaseUrl(explicitPreviewApiBaseURL);
  }

  const apiBaseURL = process.env.REACT_APP_ENV_API?.trim();
  if (apiBaseURL) {
    return `${normalizeBaseUrl(apiBaseURL)}/previewAPI`;
  }

  return 'https://niastest02.norskinteraktiv.no/previewAPI';
};

export const previewApiBaseURL = resolvePreviewApiBaseURL();
//export const previewApiBaseURL = 'https://localhost:44319/previewAPI';

export const buildPreviewApiUrl = (path: string) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${previewApiBaseURL}${normalizedPath}`;
};

export const resolvePreviewHostUrl = (rawUrl: string) => {
  const trimmedUrl = rawUrl?.trim();
  if (!trimmedUrl) {
    return rawUrl;
  }

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  if (!trimmedUrl.startsWith('/')) {
    return trimmedUrl;
  }

  try {
    return new URL(trimmedUrl, previewApiBaseURL).toString();
  } catch {
    return trimmedUrl;
  }
};

// Add a request interceptor
axios.interceptors.request.use(
  function (config) {
    if (accessToken) {
      config.headers.authorization = `Bearer ${accessToken}`;
    } else if (config.headers && 'authorization' in config.headers) {
      delete config.headers.authorization;
    }
    return config;
  },
  function (error) {
    console.log(error);
    return Promise.reject(error);
  },
);

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    SnackbarUtils.closeConnectionError();
    isConnectionSnackbar = false;
    return response;
  },
  function (error) {
    return handleError(error);
  },
);

export function setLogoutHandler(fn: () => void) {
  logoutHandler = fn;
}

/**
 * Ends the author's session the one way every host does: signs out through the handler App registered.
 * Answers a promise that never settles, so the caller's own failure handling does not run while the
 * page is leaving.
 */
export function endSession(): Promise<never> {
  if (logoutHandler) logoutHandler();
  return new Promise<never>(() => {});
}

let connectionCounter = 0;
const maxConnections = 3;
const handleError = async (error: any) => {
  store.mediaLibraryStore.finnishUploadProgress('Error');
  if (error.response) {
    console.log('error', error);
    const status = error.response.status;
    const headers = error.response.headers || {};
    const data = error.response.data || {};

    // Handle explicit session superseded from server
    if (status === 401) {
      if (isSessionSuperseded(error.response)) {
        // IMPORTANT: swallow the error so callers don't see a rejection
        return endSession();
      }

      return Promise.reject(error);
    }

    // Default error handling
    console.log(data);
    console.log(status);
    console.log(headers);
    if (data && (data as any).value) {
      SnackbarUtils.error((data as any).value);
    }
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    store.pageStore.setPageState(EPageState.disconnected);
    if (!isConnectionSnackbar) {
      console.log(error.request);
      SnackbarUtils.connectionError('Unable to connect.');
      isConnectionSnackbar = true;
    }
    console.log(error.request);
    if (connectionCounter == maxConnections) {
      SnackbarUtils.closeConnectionError();
      isConnectionSnackbar = false;
      SnackbarUtils.connectionError('Unable to connect.', false);
      return Promise.reject(error);
    }
    if (connectionCounter <= maxConnections) {
      await timeout(2000);
      connectionCounter += 1;
      store.pageStore.setPageState(EPageState.reconnecting);
      return axios.request(error.config);
    }
  } else {
    // Something happened in setting up the request that triggered an Error
    console.log('Error', error.message);
  }
  console.log(error.config);
  return Promise.reject(error);
};

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const requests = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  getFile: (url: string) =>
    axios.get(url, { responseType: 'blob' }).then((response) => {
      const blob = new Blob([response.data], { type: 'application/zip' }),
        downloadUrl = window.URL.createObjectURL(blob),
        disposition = response.headers['content-disposition'];
      let filename = 'export.zip';

      if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          matches = filenameRegex.exec(disposition);

        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      const a = document.createElement('a');
      if (typeof a.download === 'undefined') {
        window.location.href = downloadUrl;
      } else {
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
      }
    }),
  getSoundFile: (url: string, body?: any) =>
    axios
      .post(url, body, {
        headers: {
          'Content-Type': 'application/json',
        },
        responseType: 'blob',
      })
      .then((response) => {
        const contentTypeHeader = response.headers['content-type'];
        const contentType = typeof contentTypeHeader === 'string' ? contentTypeHeader.split(';')[0].trim() : Array.isArray(contentTypeHeader) ? contentTypeHeader[0]?.split(';')[0].trim() : '';
        const blob = new Blob([response.data], { type: contentType || 'audio/mpeg' });
        return blob;
      }),
  post: <T>(url: string) => axios.post<T>(url).then(responseBody),
  postFormData: <T>(
    url: string,
    formData: FormData, //TODO: Dette bør endres slik at istedet for å laste opp ale filene i en upload, så kan man splitte opp og sende en post per fil. Da er det lettere å få upload progress til å fungere.
  ) =>
    axios
      .post<T>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 1;
          const progress = (progressEvent.loaded / total) * 100;
          store.mediaLibraryStore.setUploadProgress(progress);
        },
        /*         onDownloadProgress: (progressEvent) => {
          const progress = 50 + (progressEvent.loaded / progressEvent.total) * 50;
          console.log(progress);
        }, */
      })
      .then(responseBody),
  postJson: <T>(url: string, body?: any) =>
    axios
      .post<T>(url, body, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(responseBody),
};

const Admin = {
  GetOwnerID: (auth0User: string) => requests.post<IOwnerIdModel[]>(`/Administrator/GetOwnerID?Auth0User=${auth0User}`),
  SetTestOwner: (auth0User: string, testOwnerID: number) => requests.get(`/Administrator/SetTestOwner?Auth0User=${auth0User}&TestOwnerID=${testOwnerID}`),
  GetTestOwner: () => requests.get<ITestOwner>('/Administrator/GetTestOwner'),
};

const Proxy = {
  AzureTTS: (text: string, language: string, voice: string, format: string) =>
    requests.getSoundFile(
      '/Proxy/AzureTTS',
      JSON.stringify({
        text,
        language,
        voice,
        format,
      }),
    ),
  GetGoogleVoices: (languageCode: string, model: string) =>
    requests.get<GoogleVoiceCatalogApiResponse | { voices: GoogleVoiceOption[] } | GoogleVoiceOption[]>(
      `/Proxy/GetGoogleVoices?languageCode=${encodeURIComponent(languageCode)}&model=${encodeURIComponent(model)}`,
    ),
  GoogleTTS: (payload: GoogleSynthesisRequest) => requests.getSoundFile('/Proxy/GoogleTTS', JSON.stringify(payload)),
};

const Owner = {
  createCourse: (obj: string) => requests.postJson<TOwnerCourseViewModel[]>('/Owner/CreateCourse', obj),
  copyCourse: (courseID: number) => requests.post<TOwnerCourseViewModel[]>(`/Owner/CopyCourse?CourseID=${courseID}`),
  getCourses: (sortBy: EOwnerCourseListSortByType, searchQuery: string, CourseSharedByType: ECourseViewType) =>
    requests.get<TOwnerCourseViewModel[]>(`/Owner/GetCourses?SortByType=${sortBy}&SearchString=${searchQuery}&CourseSharedByType=${ECourseViewType[CourseSharedByType]}`),
  getCourse: (id: number) => requests.get<TOwnerCourseViewModel[]>(`/Owner/GetCourses?CourseID=${id}`),
  getNewCourseWizard: () => requests.get<ICourseWizard>('/Owner/GetNewCourseWizard'),
  getToolBox: () => console.log('not implemented') /* requests.get<TToolBox>('/Owner/GetToolBox') */,
  deleteCourse: (id: number) => requests.post(`/Owner/DeleteCourse?CourseID=${id}`),
  setShareCourses: (shareNameIDList: string[], courseIDList: number[], sharedLevel: number) =>
    requests.postJson<undefined>('/Owner/SetShareCourses', JSON.stringify({ shareNameIDList, courseIDList, sharedLevel })),
  findOwner: (email: string) => requests.postJson<IFindOwner>('/Owner/FindOwner', JSON.stringify({ searchField: 'email', searchFor: email })),
  getSharedCourses: (_obj: string) => requests.postJson<undefined>(''),
  getSharedUsers: (courseID: number) => requests.postJson<ISharedUsers>('/Owner/GetSharedUsers', JSON.stringify({ courseID: courseID })),
  takeOwnerShip: (_obj: string) => requests.postJson<undefined>(''),
};

const Course = {
  getProperties: (id: number) => requests.get<ICourseProperties>(`/Course/GetProperties?CourseID=${id}`),
  getPages: (id: number) => requests.get<TCourseViewModel>(`/Course/GetPages?CourseID=${id}`),
  movePage: (courseID: number, coursePageID: string, parentCoursePageID: string, index?: number) =>
    requests.postJson('/Course/MovePage', JSON.stringify({ courseID: courseID, coursePageID: coursePageID, parentCoursePageID: parentCoursePageID, index: index !== undefined ? index : null })),
  newPage: (newPageRequest: INewPageModel) => requests.postJson<TPageViewModel>('/Course/NewPage', JSON.stringify(newPageRequest)),
  removePage: (courseID: number, coursePageID?: string) => requests.postJson('/Course/RemovePage', JSON.stringify({ courseID: courseID, coursePageID: coursePageID })),
  copyPage: (copyObject: ICopyPageObject) => requests.postJson<ICopyPageObject>('/Course/CopyPage', JSON.stringify(copyObject)),
};

const Page = {
  getPage: (courseID: number, coursePageID: number) => requests.get<TPageViewModel>(`/Page/GetPage?CourseID=${courseID}&CoursePageID=${coursePageID}`),
  getProperties: (courseID: number, coursePageID: number) => requests.get<IPageProperties>(`/Page/GetProperties?CourseID=${courseID}&CoursePageID=${coursePageID}`),
  GetPagePlaceholderProperties: (courseID: number, coursePageID: number, pagePlaceholderID: number) =>
    requests.get<IPlaceholderProperties>(`/Page/GetPagePlaceholderProperties?CourseID=${courseID}&CoursePageID=${coursePageID}&PagePlaceholderID=${pagePlaceholderID}`),
  GetPagePlaceholderContentProperties: (courseID: number, coursePageID: number, pagePlaceholderID: number, contentID: number) =>
    requests.get<IContentProperties>(`/Page/GetPagePlaceholderContentProperties?CourseID=${courseID}&CoursePageID=${coursePageID}&PagePlaceholderID=${pagePlaceholderID}&ContentID=${contentID}`),
  getPageFetchState: (courseID: number, fetchPageState: number) => requests.get<TPageViewModel>(`/Page/GetPage?CourseID=${courseID}&FetchPageState=${fetchPageState}`),
  NewPlaceholder: (pageID: number) => requests.post<IPagePlaceholder>(`/Page/NewPlaceholder?PageID=${pageID}`),
  DeletePlaceholder: (pageID: number, pagePlaceholderID: number) => requests.post(`/Page/DeletePlaceholder?PageID=${pageID}&PagePlaceholderID=${pagePlaceholderID}`),
  /*   NewContent: (pageID: number, pagePlaceholderID: number, coursePageContentType: EContentType) =>
    requests.post<IContent>(`/Page/NewContent?PageID=${pageID}&PagePlaceholderID=${pagePlaceholderID}&CoursePageContentType=${coursePageContentType}`), */
  NewContent: (courseID: number, coursePageID: number, pagePlaceholderID: number, coursePageContentType: EContentType, newContentProperties?: INewContentProperties) =>
    requests.postJson<IContent>(
      '/Page/NewContent',
      JSON.stringify({ courseID: courseID, coursePageID: coursePageID, pagePlaceholderID: pagePlaceholderID, coursePageContentType: coursePageContentType, properties: newContentProperties }),
    ),

  DeleteContent: (courseID: number, coursePageID: number, pagePlaceholderID: number, contentID: number) =>
    requests.postJson('/Page/DeletePagePlaceholderContent', JSON.stringify({ courseID: courseID, coursePageID: coursePageID, pagePlaceholderID: pagePlaceholderID, contentID: contentID })),
  updateContent: (content: IContent) => requests.postJson<IContent>('/Page/UpdateContent', JSON.stringify(content)),
  updateContents: (contents: IContent[]) => requests.postJson<IContent>('/Page/UpdateContents', JSON.stringify(contents)),
  updatePlaceholder: (Placeholder: IPagePlaceholder) => requests.postJson<IPagePlaceholder>('/Page/UpdatePlaceholder', JSON.stringify(Placeholder)),
  changePageLayout: (courseID: number, coursePageID: number, newLayoutNo: number) =>
    requests.postJson('/Page/ChangePageLayout', JSON.stringify({ courseID: courseID, coursePageID: coursePageID, newLayoutNo: newLayoutNo })),
  NewAccordion: (courseID: number, coursePageID: number, pagePlaceholderID: number, enquiryContentType: string, newContentProperties?: INewContentProperties) =>
    requests.postJson<IContent>(
      '/Page/NewAccordion',
      JSON.stringify({
        enquiryID: courseID,
        courseID: courseID,
        coursePageID: coursePageID,
        pagePlaceholderID: pagePlaceholderID,
        enquiryContentType: enquiryContentType,
        properties: newContentProperties,
      }),
    ),
  NewAccordionSection: (courseID: number, enquiryID: number, enquiryContentID: number, coursePageID: number, pagePlaceholderID: number, enquiryContentType: string) =>
    requests.postJson<IContent>(
      '/Page/NewAccordionSection',
      JSON.stringify({
        enquiryID: enquiryID,
        courseID: courseID,
        coursePageID: coursePageID,
        pagePlaceholderID: pagePlaceholderID,
        enquiryContentType: enquiryContentType,
        questionEnquiryID: enquiryContentID,
      }),
    ),
  //TODO: enquiryID vil alltid være 1. Men den må fikses etterhvert.
  NewQuestion: (courseID: number, coursePageID: number, pagePlaceholderID: number, enquiryContentType: string, newContentProperties?: INewContentProperties) =>
    requests.postJson<IContent>(
      '/Page/NewQuestion',
      JSON.stringify({
        enquiryID: courseID,
        courseID: courseID,
        coursePageID: coursePageID,
        pagePlaceholderID: pagePlaceholderID,
        enquiryContentType: enquiryContentType,
        properties: newContentProperties,
      }),
    ),
  NewAnswer: (courseID: number, enquiryID: number, enquiryContentID: number, coursePageID: number, pagePlaceholderID: number, enquiryContentType: string) =>
    requests.postJson<IContent>(
      '/Page/NewAnswer',
      JSON.stringify({
        enquiryID: enquiryID,
        courseID: courseID,
        coursePageID: coursePageID,
        pagePlaceholderID: pagePlaceholderID,
        enquiryContentType: enquiryContentType,
        questionEnquiryID: enquiryContentID,
      }),
    ),
  NewDragDrop: (categorizationData: string) => requests.postJson<IContent>('/Page/NewDragDrop', categorizationData),
  GetCSSNames: () => requests.get<ICssCaption[]>('/Page/GetCSSNames'),
  UpdateCoursePageHtml: (exportData: ICoursePageExportData) => requests.postJson('/Page/UpdateCoursePageHtml', JSON.stringify(exportData)),
  CopyContent: (copyObject: ICopyObject) => requests.postJson('/Page/CopyContent', JSON.stringify(copyObject)),
  SetExpanded: (courseID: number, coursePageID: number, isExpanded: boolean) =>
    requests.postJson('page/SetExpanded', JSON.stringify({ courseID: courseID, coursePageID: coursePageID, isExpanded: isExpanded })),
};

const Property = {
  setOption: (value: string, propertyValueID?: number, valueMapID?: IValueMapID) =>
    requests.postJson<IPropertyValueObject>('/Property/SetValue', JSON.stringify({ propertyValueID: propertyValueID, value: value, valueMapID: valueMapID })),
  setOptions: (options: IPropertyValueObject[]) => requests.postJson<IPropertyValueObject[]>('/Property/SetValues', JSON.stringify(options)),
  uploadPhoto: (propertyValueID: number, folder: string, file: any) => {
    const formData = new FormData();
    formData.append('UploadFile', file);
    return axios.post(`/Property/UploadFile?PropertyValueID=${propertyValueID}&Folder=${folder}`, formData, {
      headers: { 'Content-type': 'multipart/form-data' },
    });
  },
  UploadFileFromUrl: (propertyValueID: number, folder: string, url: string) =>
    requests.postJson('/Property/UploadFileFromUrl', JSON.stringify({ propertyValueID: propertyValueID, folder: folder, url: url })),
};

const Enquiry = {
  getEnquiryProperties: (enquiryID: number) => requests.get<EnquiryProperties>(`/Enquiry/getEnquiryProperties?EnquiryID=${enquiryID}`),
  getCourseEnquiryProperties: (courseID: number, enquiryID: number) => requests.get(/*NO TYPE YET*/ `/Enquiry/getCourseEnquiryProperties?CourseID=${courseID}&EnquiryID=${enquiryID}`),
  getEnquiryContentProperties: (contentID: number) => requests.get<EnquiryContentProperties>(`/Enquiry/getEnquiryContentProperties?ContentID=${contentID}`),
  getEnquiryContentObject: (contentObject: IEnquiryQuestionProperties) => requests.postJson<IEnquiryContentObjectProperties>('/Enquiry/GetEnquiryContentObject', JSON.stringify(contentObject)),
  updateEnquiryContentObject: (contentObject: IEnquiryQuestionProperties) => requests.postJson<IEnquiryContentObjectProperties>('/Enquiry/UpdateEnquiryContentObject', JSON.stringify(contentObject)),
  moveEnquiryContent: (MoveEnquiryObj: IMoveEnquiryObj) => requests.postJson<IContent>('/Enquiry/MoveEnquiryContent', JSON.stringify(MoveEnquiryObj)),
};

const ExportStaticHtml = {
  PreviewPage: (courseID: number, coursePageID: number) => requests.get<string>(`/ExportStaticHtml/PreviewPage?CourseID=${courseID}&CoursePageID=${coursePageID}`),
  ExportToSCORM: (courseID: number, version: string, language: string) => requests.get(`/ExportStaticHtml/ExportToSCORM?CourseID=${courseID}&Version=${version}&Language=${language}`),
  ExportToHTML: (courseID: number, version: string, language: string) => requests.get(`/ExportStaticHtml/ExportToHTML?CourseID=${courseID}&Version=${version}&Language=${language}`),
  GetExportPackageFile: (courseID: number, courseExportID: number) => requests.getFile(`/ExportStaticHtml/GetExportPackageFile?CourseID=${courseID}&CourseExportID=${courseExportID}`),
  GetExportPackages: (courseID: number) => requests.get<ICourseExportPackages[]>(`/ExportStaticHtml/GetExportPackages?CourseID=${courseID}`),
  PurgeExportPackage: (courseID: number, courseExportID: number) => requests.postJson('/ExportStaticHtml/PurgeExportPackage', JSON.stringify({ courseID: courseID, courseExportID: courseExportID })),
};

const previewRequests = {
  get: <T>(url: string) =>
    axios
      .get<T>(`${previewApiBaseURL}${url}`, {
        withCredentials: true,
      })
      .then(responseBody),
  post: <T>(url: string) =>
    axios
      .post<T>(`${previewApiBaseURL}${url}`, undefined, {
        withCredentials: true,
      })
      .then(responseBody),
  postJson: <T>(url: string, body?: any) =>
    axios
      .post<T>(`${previewApiBaseURL}${url}`, body, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      })
      .then(responseBody),
  putJson: <T>(url: string, body?: any) =>
    axios
      .put<T>(`${previewApiBaseURL}${url}`, body, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      })
      .then(responseBody),
  delete: <T>(url: string) =>
    axios
      .delete<T>(`${previewApiBaseURL}${url}`, {
        withCredentials: true,
      })
      .then(responseBody),
};

type TPreviewShareLinkSummaryResponse = {
  publicId: string;
  previewName?: string;
  courseId: number;
  coursePageId?: number;
  createdByOwnerId: number;
  language: string;
  accessMode: number;
  commentVisibilityMode?: number;
  allowComments?: boolean;
  privateComments?: boolean;
  requiresPassword: boolean;
  expiresAtUtc: string | null;
  maxViews?: number;
  viewCount: number;
  revokedAtUtc?: string;
  createdAtUtc: string;
  lastAccessAtUtc?: string;
  isExpired: boolean;
  isRevoked: boolean;
  invitedEmails: string[];
};

type TPreviewShareLinkMetaResponse = {
  previewName?: string;
  courseId?: number;
  coursePageId?: number;
  accessMode: number;
  allowComments: boolean;
  privateComments: boolean;
  requiresPassword: boolean;
  expiresAtUtc: string | null;
  isExpired: boolean;
  isRevoked: boolean;
};

const Preview = {
  CreateInternalSession: (courseID: number, coursePageID: number, language?: string) =>
    previewRequests.postJson<{ launchUrl: string; expiresAtUtc: string }>(
      '/PreviewSession/InternalSession',
      JSON.stringify({ courseId: courseID, coursePageId: coursePageID, language: language ?? null }),
    ),
  CreateShareLink: (
    courseID: number,
    coursePageID: number | undefined,
    language: string,
    expiresAtUtc: string | null,
    password?: string,
    maxViews?: number,
    previewName?: string,
    accessMode = 1,
    allowComments = true,
    privateComments = false,
    invitedEmails?: string[],
    publicId?: string,
  ) =>
    previewRequests.postJson<TPreviewShareLinkSummaryResponse>(
      '/PreviewShare/ShareLinks',
      JSON.stringify({
        courseId: courseID,
        coursePageId: coursePageID ?? null,
        publicId: publicId ?? null,
        language,
        previewName: previewName ?? null,
        accessMode,
        allowComments,
        privateComments,
        expiresAtUtc,
        password: password ?? null,
        maxViews: maxViews ?? null,
        invitedEmails: invitedEmails ?? [],
      }),
    ),
  ListShareLinks: (courseID: number, includeRevoked = true) =>
    previewRequests.get<{ items: TPreviewShareLinkSummaryResponse[] }>(`/PreviewShare/ShareLinks?courseId=${courseID}&includeRevoked=${includeRevoked}`),
  UpdateShareLink: (
    publicId: string,
    coursePageID: number | undefined,
    language: string,
    expiresAtUtc: string | null,
    password?: string,
    removePassword = false,
    maxViews?: number,
    previewName?: string,
    accessMode = 1,
    allowComments = true,
    privateComments = false,
    invitedEmails?: string[],
  ) =>
    previewRequests.putJson<TPreviewShareLinkSummaryResponse>(
      `/PreviewShare/ShareLinks/${encodeURIComponent(publicId)}`,
      JSON.stringify({
        coursePageId: coursePageID ?? null,
        language,
        previewName: previewName ?? null,
        accessMode,
        allowComments,
        privateComments,
        expiresAtUtc,
        password: password ?? null,
        removePassword,
        maxViews: maxViews ?? null,
        invitedEmails: invitedEmails ?? [],
      }),
    ),
  GetShareLinkMeta: (publicId: string) => previewRequests.get<TPreviewShareLinkMetaResponse>(`/PreviewShare/ShareLinks/${encodeURIComponent(publicId)}/Meta`),
  UnlockShareLink: (publicId: string, password?: string) =>
    previewRequests.postJson<{ launchUrl: string; expiresAtUtc: string }>(`/PreviewShare/ShareLinks/${encodeURIComponent(publicId)}/Unlock`, JSON.stringify({ password: password ?? null })),
  RevokeShareLink: (publicId: string) => previewRequests.delete<void>(`/PreviewShare/ShareLinks/${encodeURIComponent(publicId)}`),
  DeleteShareLink: (publicId: string) => previewRequests.delete<void>(`/PreviewShare/ShareLinks/${encodeURIComponent(publicId)}/Permanent`),
  LogoutSession: () => previewRequests.post<void>('/PreviewSession/Logout'),
};

const PreviewComment = {
  ListByPage: (coursePageId: number) => previewRequests.get<{ items: IPreviewComment[] }>(`/PreviewComment/Comments?coursePageId=${coursePageId}`),
  Create: (coursePageId: number, body: string, parentPreviewCommentId?: number, identity?: TPreviewCommentIdentityPayload) =>
    previewRequests.postJson<IPreviewComment>(
      '/PreviewComment/Comments',
      JSON.stringify({
        coursePageId,
        body,
        parentPreviewCommentId: parentPreviewCommentId ?? null,
        name: identity?.name?.trim() || null,
        email: identity?.email?.trim() || null,
      }),
    ),
  AddReaction: (previewCommentId: number, coursePageId: number, reactionCode: string, identity: TPreviewCommentIdentityPayload) =>
    previewRequests.postJson<IPreviewCommentReaction>(
      `/PreviewComment/Comments/${encodeURIComponent(`${previewCommentId}`)}/Reactions/${encodeURIComponent(reactionCode)}`,
      JSON.stringify({
        coursePageId,
        name: identity?.name?.trim() || null,
        email: identity?.email?.trim() || null,
      }),
    ),
  RemoveReaction: (previewCommentId: number, coursePageId: number, reactionCode: string, identity: TPreviewCommentIdentityPayload) =>
    previewRequests.postJson<IPreviewCommentReaction>(
      `/PreviewComment/Comments/${encodeURIComponent(`${previewCommentId}`)}/Reactions/${encodeURIComponent(reactionCode)}/Remove`,
      JSON.stringify({
        coursePageId,
        name: identity?.name?.trim() || null,
        email: identity?.email?.trim() || null,
      }),
    ),
  ListByPageInternal: (courseId: number, coursePageId: number) =>
    previewRequests.get<{ items: IPreviewComment[] }>(`/PreviewComment/InternalComments?courseId=${courseId}&coursePageId=${coursePageId}`),
  CreateInternal: (courseId: number, coursePageId: number, body: string, parentPreviewCommentId?: number) =>
    previewRequests.postJson<IPreviewComment>(
      '/PreviewComment/InternalComments',
      JSON.stringify({
        courseId,
        coursePageId,
        body,
        parentPreviewCommentId: parentPreviewCommentId ?? null,
      }),
    ),
  AddReactionInternal: (previewCommentId: number, courseId: number, coursePageId: number, reactionCode: string) =>
    previewRequests.post<IPreviewCommentReaction>(
      `/PreviewComment/InternalComments/${encodeURIComponent(`${previewCommentId}`)}/Reactions/${encodeURIComponent(reactionCode)}?courseId=${courseId}&coursePageId=${coursePageId}`,
    ),
  RemoveReactionInternal: (previewCommentId: number, courseId: number, coursePageId: number, reactionCode: string) =>
    previewRequests.post<IPreviewCommentReaction>(
      `/PreviewComment/InternalComments/${encodeURIComponent(`${previewCommentId}`)}/Reactions/${encodeURIComponent(reactionCode)}/Remove?courseId=${courseId}&coursePageId=${coursePageId}`,
    ),
  DeleteInternal: (previewCommentId: number, courseId: number, coursePageId: number) =>
    previewRequests.delete<void>(`/PreviewComment/InternalComments/${encodeURIComponent(`${previewCommentId}`)}?courseId=${courseId}&coursePageId=${coursePageId}`),
};

const Media = {
  createFolder: (path: string) => requests.postJson<LSResponseModel>('/Media/RunFTPCommand', { mkd: { path: path } }),
  getFolders: () => requests.postJson<LSResponseModel>('/Media/RunFTPCommand', { ls: { path: '', pattern: '.', options: '/S' } }),
  getFilesInFolder: (path: string) => requests.postJson<LSResponseModel>('/Media/RunFTPCommand', { dir: { path: path, pattern: '*' } }),
  getFileInFolder: (path: string, fileName: string) => requests.postJson<LSResponseModel>('/Media/RunFTPCommand', { dir: { path: path, pattern: fileName } }),
  rename: (oldPath: string, newPath: string, option: string) => requests.postJson<LSResponseModel>('/Media/RunFTPCommand', { ren: { oldPath: oldPath, newPath: newPath, options: option } }),
  move: (oldPath: string, newPath: string) => requests.postJson<LSResponseModel>('/Media/RunFTPCommand', { ren: { oldPath: oldPath, newPath: newPath, options: '/F' } }),
  deleteFolder: (path: string) => requests.postJson<LSResponseModel>('/Media/RunFTPCommand', { del: { paths: [path], options: '/D' } }),
  deleteFile: (path: string) => requests.postJson<LSResponseModel>('/Media/RunFTPCommand', { del: { paths: [path], options: '/F' } }),
  uploadFile: (path: string, file: FormData) => requests.postFormData<IUploadFileResponseModel>(`/Media/UploadFile?folder=${path}`, file),
  uploadFiles: (path: string, files: FormData) => requests.postFormData<IUploadFileResponseModel>(`/Media/UploadFile?folder=${path}`, files),
  getLocalPathFromURL: (url: string) => requests.get<ILocalPathModel>(`/Media/GetLocalPathFromURL?URL=${url}`),
};

const User = {
  getUserProfileView: () => requests.get<IUserProfileViewModel>('/User/GetUserProfileView'),
  getWhoAmI: () => requests.get<IWhoAmIResponse>('/Owner/WhoAmI'),
  changePassword: (email: string) => requests.post<IUserPasswordChangeResponse>(`/User/ChangePassword?Email=${email}`),
  setUserMetadata: (metadata: IMetadata) => requests.postJson('/User/SetUserMetadata', JSON.stringify(metadata)),
  setUserData: (user: CreatorUser) => requests.postJson('/User/SetUserData', JSON.stringify(user)),
  SetUserAvatar: (file: any) => {
    const formData = new FormData();
    formData.append('UploadFile', file);
    return axios
      .post<CreatorUser>('/User/SetUserAvatar', formData, {
        headers: { 'Content-type': 'multipart/form-data' },
      })
      .then(responseBody);
  },
  GetAllUserRoles: () => requests.get<IRoles[]>('/User/GetAllUserRoles'),
  GetAllUsers: () => requests.get<IUser[]>('/User/GetAllUsers'),
};

const agent = {
  Owner,
  Course,
  Page,
  Property,
  Enquiry,
  User,
  Media,
  ExportStaticHtml,
  Preview,
  PreviewComment,
  Admin,
  Proxy,
};

export default agent;

export type TPreviewCommentIdentityPayload = {
  name?: string | null;
  email?: string | null;
};

export interface INewContentProperties {
  paddingLeft?: string;
  paddingRight?: string;
  paddingTop?: string;
  paddingBottom?: string;
}
